import { Element, useNode } from "@craftjs/core";
import { rem } from "polished";
import * as React from "react";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import type { ImagePosition } from "@/components/editor/image-positioner/image-positioner-component";
import Action, {
  ActionComponentStyle,
  ACTIONS,
} from "@/components/editor/primitives/legacy/action/action-component";
import RichText from "@/components/editor/primitives/rich-text";
import Text from "@/components/editor/primitives/text/text-component";
import { DEFAULT_BUTTON_TEXT } from "@/components/editor/shared/constants";
import { whiteLogo } from "@/components/editor/shared/header/assets";
import formatLogoSize from "@/components/editor/shared/header/format-logo-size";
import HeaderSettings, {
  ImageFit,
} from "@/components/editor/shared/header/header-settings-component";
import useRichTextCompatible from "@/hooks/use-rich-text-compatible";

import {
  CTAButtonWrapper,
  CTAWrapper,
  LogoContainer,
  RichTextContainer,
  Wrapper,
} from "./brand-maxi-cover-component-styles";

export type BrandMaxiCoverProps = {
  logo?: string;
  heading: string;
  subheading: string;
  ctaText: string;
  backgroundImage?: string;
  backgroundPosition?: ImagePosition;
  backgroundColor?: string;
  isLogoShowing?: boolean;
  logoSize?: number;
  imageFit?: ImageFit;
};

const CustomHeaderSettings = () => (
  <HeaderSettings optimalImageSize="1440×800px" disableBackgroundImageAlt />
);

export default function BrandMaxiCover(props: BrandMaxiCoverProps) {
  const {
    connectors: { connect },
    id: nodeId,
  } = useNode();

  const ctaButtonStyle = React.useMemo<ActionComponentStyle>(
    () => ({
      lineHeight: rem(28),
      padding: [
        `${rem(8)} ${rem(48)}`,
        `${rem(8)} ${rem(48)}`,
        `${rem(8)} ${rem(48)}`,
      ],
    }),
    []
  );

  const isRichTextCompatible = useRichTextCompatible(nodeId, [
    "heading",
    "subheading",
    "cta-text",
  ]);

  return (
    <Wrapper
      ref={connect}
      id={nodeId}
      data-testid="brand-maxi-cover"
      $backgroundImage={props.backgroundImage}
      backgroundPosition={props.backgroundPosition}
      imageFit={props.imageFit}
    >
      {props.isLogoShowing && (
        <LogoContainer isLogoResized={!isNaN(props.logoSize)}>
          <img
            draggable="false"
            src={props.logo || whiteLogo}
            alt="Header Logo"
            data-testid="brand-maxi-cover-logo"
            style={{
              height: formatLogoSize(props.logoSize),
              width: "auto",
            }}
          />
        </LogoContainer>
      )}

      {isRichTextCompatible ? (
        <RichTextContainer>
          <div data-testid="brand-maxi-cover-heading">
            <Element
              id="heading"
              is={RichText}
              text={`<h2 style='text-align:center;'>${props.heading}</h2>`}
            />
          </div>
          <Element
            id="subheading"
            is={RichText}
            text={`<p style='text-align:center;'>${props.subheading}</p>`}
          />

          <Element
            id="cta-text"
            is={RichText}
            text={`<h5 style='text-align:center;'>${props.ctaText}</h5>`}
          />
        </RichTextContainer>
      ) : (
        <>
          <Element
            id="heading"
            is={Text}
            element="h1"
            text={props.heading}
            pattern="headingOne"
          />
          <Element
            id="subheading"
            is={Text}
            text={props.subheading}
            pattern="paragraph"
          />
          <CTAWrapper>
            <Element
              id="cta-text"
              is={Text}
              text={props.ctaText}
              element="h4"
              pattern="headingFour"
            />
          </CTAWrapper>
        </>
      )}
      <CTAButtonWrapper>
        <Element
          id="cta-button"
          is={Action}
          element="button"
          text={DEFAULT_BUTTON_TEXT}
          style={ctaButtonStyle}
          action={ACTIONS.registrationPage}
          actions={[ACTIONS.registrationPage, ACTIONS.externalLink]}
          showOptions
        />
      </CTAButtonWrapper>
    </Wrapper>
  );
}

BrandMaxiCover.craft = {
  custom: {
    displayName: "Brand Maxi Cover",
  },
  related: {
    settings: CustomHeaderSettings,
    leftOutlineOptions: TopLevelFloatingOptions,
  },
};
